import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["data", "signupsChart", "engagementChart", "notificationsChart"]

  connect() {
    this.charts = []
    this.resizeHandler = () => this.charts.forEach((chart) => chart.resize())

    if (typeof echarts === "undefined" || !this.hasDataTarget) return

    try {
      this.chartData = JSON.parse(this.dataTarget.textContent)
      this.renderCharts()
      window.addEventListener("resize", this.resizeHandler)
    } catch (error) {
      console.error("Product dashboard charts could not be initialized", error)
    }
  }

  disconnect() {
    window.removeEventListener("resize", this.resizeHandler)
    this.charts?.forEach((chart) => chart.dispose())
  }

  renderCharts() {
    const colors = this.themeColors()

    if (this.hasSignupsChartTarget) {
      this.createChart(this.signupsChartTarget, this.lineOption(
        this.chartData.signups || [],
        [{ key: "value", label: "New accounts", color: colors.blue }],
        colors
      ))
    }

    if (this.hasEngagementChartTarget) {
      this.createChart(this.engagementChartTarget, this.barOption(
        this.chartData.engagement || [],
        [
          { key: "shared_subscriptions", label: "Shared subscriptions", color: colors.blue },
          { key: "custom_feeds", label: "Custom feeds", color: colors.teal },
          { key: "bookmarks", label: "Bookmarks", color: colors.orange }
        ],
        colors
      ))
    }

    if (this.hasNotificationsChartTarget) {
      this.createChart(this.notificationsChartTarget, this.lineOption(
        this.chartData.notifications || [],
        [
          { key: "scheduled", label: "Scheduled", color: colors.navy },
          { key: "opened", label: "Opened", color: colors.green }
        ],
        colors
      ))
    }
  }

  createChart(element, option) {
    const chart = echarts.init(element)
    chart.setOption(option)
    this.charts.push(chart)
  }

  lineOption(rows, series, colors) {
    return this.baseOption(rows, series, colors, "line")
  }

  barOption(rows, series, colors) {
    return this.baseOption(rows, series, colors, "bar")
  }

  baseOption(rows, series, colors, type) {
    if (!rows.length || rows.every((row) => series.every((item) => Number(row[item.key] || 0) === 0))) {
      return this.emptyOption(colors)
    }

    return {
      animationDuration: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? 0 : 420,
      color: series.map((item) => item.color),
      tooltip: {
        trigger: "axis",
        backgroundColor: colors.surface,
        borderColor: colors.border,
        textStyle: { color: colors.ink }
      },
      legend: {
        bottom: 0,
        left: 0,
        textStyle: { color: colors.muted, fontSize: 11 }
      },
      grid: { left: 44, right: 16, top: 16, bottom: 54 },
      xAxis: {
        type: "category",
        data: rows.map((row) => this.shortDate(row.date)),
        axisLine: { lineStyle: { color: colors.border } },
        axisLabel: { color: colors.muted, fontSize: 10, hideOverlap: true }
      },
      yAxis: {
        type: "value",
        minInterval: 1,
        axisLabel: { color: colors.muted },
        splitLine: { lineStyle: { color: colors.border, type: "dashed" } }
      },
      series: series.map((item) => ({
        name: item.label,
        type,
        data: rows.map((row) => Number(row[item.key] || 0)),
        smooth: type === "line",
        symbol: type === "line" ? "circle" : undefined,
        symbolSize: 6,
        barMaxWidth: 18,
        lineStyle: type === "line" ? { width: 3 } : undefined,
        areaStyle: type === "line" ? { opacity: 0.06 } : undefined,
        emphasis: { focus: "series" }
      }))
    }
  }

  emptyOption(colors) {
    return {
      animation: false,
      title: {
        text: "No activity in this period",
        subtext: "The dashboard will populate as records arrive.",
        left: "center",
        top: "center",
        textStyle: { color: colors.ink, fontSize: 14, fontWeight: 700 },
        subtextStyle: { color: colors.muted, fontSize: 11 }
      },
      xAxis: { show: false },
      yAxis: { show: false },
      series: []
    }
  }

  themeColors() {
    const styles = getComputedStyle(document.body)
    const value = (name, fallback) => styles.getPropertyValue(name).trim() || fallback

    return {
      blue: value("--nfr-page-blue", "#2f80ed"),
      navy: value("--nfr-page-navy", "#0e3858"),
      teal: "#057f95",
      green: value("--nfr-ui-success", "#2f6f59"),
      orange: value("--nfr-ui-warning", "#a15c00"),
      ink: value("--nfr-page-ink", "#141824"),
      muted: value("--nfr-page-muted", "#5b6578"),
      border: value("--nfr-page-border", "rgba(15, 23, 42, 0.1)"),
      surface: value("--nfr-page-surface", "#ffffff")
    }
  }

  shortDate(value) {
    const [year, month, day] = value.split("-").map(Number)
    return new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric" })
      .format(new Date(year, month - 1, day))
  }
}
